import { fetchLatestScriptSources, getCurrentScriptSources, hasScriptSetChanged } from './updateCheck';

const RETRY_KEY = 'booking:auto-update-attempt';
const EDITABLE = 'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select, [contenteditable="true"], [role="combobox"], [role="slider"], [role="switch"], [role="checkbox"]';
const writes = method => !['GET', 'HEAD', 'OPTIONS'].includes(String(method || 'GET').toUpperCase());
const SAVED_EVENT = 'booking:changes-saved';
const controlValue = node => ['checkbox', 'radio'].includes(node.type) ? node.checked
  : node.getAttribute('aria-checked') ?? node.getAttribute('aria-valuenow') ?? node.value ?? node.textContent;

// Call the returned acknowledgement only after all writes for this form have succeeded.
// Capturing before the request ensures subsequent edits remain protected.
export function captureUpdateEdits(root) {
  const edits = Array.from(root?.querySelectorAll(EDITABLE) || [], node => ({ node, value: controlValue(node) }));
  return () => document.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: edits }));
}

// Conservatively keep changed controls protected until they are reset or leave the DOM.
export function startAutomaticUpdates({
  fetchScripts = fetchLatestScriptSources,
  currentScripts = getCurrentScriptSources(),
  reload = () => window.location.reload(),
  now = Date.now,
  checkInterval = 60000,
  idleTime = 5000,
} = {}) {
  let stopped = false, checking = false, pending = '', reloading = false, mutations = 0, lastActivity = now();
  const initial = new WeakMap(), dirty = new Set();
  const valueOf = controlValue;
  const remember = event => {
    const node = event.target?.closest?.(EDITABLE);
    if (node && !initial.has(node)) initial.set(node, valueOf(node));
  };
  const changed = event => {
    const node = event.target?.closest?.(EDITABLE);
    if (!node) return;
    // If a widget did not expose its initial value, protect it rather than guessing.
    if (!initial.has(node) || initial.get(node) !== valueOf(node)) dirty.add(node);
    else dirty.delete(node);
  };
  const activity = () => { lastActivity = now(); };
  const sensitivePublicPage = () => /^\/(register|book|studio|form|f|angebot-annehmen|datencheck|hv|honorarvereinbarung)(\/|$)/.test(window.location.pathname);
  const tryReload = () => {
    if (stopped || !pending || reloading || mutations || document.visibilityState === 'hidden' || now() - lastActivity < idleTime) return;
    for (const node of dirty) {
      if (!node.isConnected || (initial.has(node) && initial.get(node) === valueOf(node))) dirty.delete(node);
    }
    const onboarding = window.location.pathname === '/setup' || new URLSearchParams(window.location.search).get('setup') === '1';
    if (dirty.size || sensitivePublicPage() || onboarding || document.activeElement?.matches?.(EDITABLE)
        || document.querySelector('[role="dialog"], [role="alertdialog"], [aria-busy="true"], [data-modal-loading="true"], [data-send-feedback], [data-setup-tour-controls]')) return;
    try {
      const previous = JSON.parse(sessionStorage.getItem(RETRY_KEY) || 'null');
      if (previous?.key === pending && now() - previous.at < 300000) return;
      sessionStorage.setItem(RETRY_KEY, JSON.stringify({ key: pending, at: now() }));
    } catch { /* Reload still works when browser storage is unavailable. */ }
    reloading = true;
    reload();
  };
  const check = async () => {
    if (stopped || checking || document.visibilityState === 'hidden') return;
    checking = true;
    try {
      const latest = await fetchScripts();
      if (!stopped) pending = hasScriptSetChanged(latest, currentScripts) ? latest.join('|') : '';
    } catch { /* An unavailable manifest is not evidence of a new build. */ }
    finally { checking = false; }
    tryReload();
  };

  // Cover Axios/XHR and fetch writes, including operations without a visible dialog.
  const originalFetch = window.fetch;
  const trackedFetch = async (input, options) => {
    const mutation = writes(options?.method || input?.method);
    if (mutation) mutations += 1;
    try { return await originalFetch.call(window, input, options); }
    finally { if (mutation) { mutations -= 1; activity(); } }
  };
  if (originalFetch) window.fetch = trackedFetch;
  const originalOpen = XMLHttpRequest.prototype.open, originalSend = XMLHttpRequest.prototype.send;
  const methods = new WeakMap();
  function trackedOpen(method, ...args) { methods.set(this, method); return originalOpen.call(this, method, ...args); }
  function trackedSend(...args) {
    if (!writes(methods.get(this))) return originalSend.apply(this, args);
    mutations += 1;
    let settled = false;
    const finish = () => { if (!settled) { settled = true; mutations -= 1; activity(); } };
    this.addEventListener('loadend', finish, { once: true });
    try { return originalSend.apply(this, args); } catch (error) { finish(); throw error; }
  }
  XMLHttpRequest.prototype.open = trackedOpen;
  XMLHttpRequest.prototype.send = trackedSend;
  const visible = () => { if (document.visibilityState !== 'hidden') void check(); };
  const reset = event => { for (const node of dirty) if (node.form === event.target) dirty.delete(node); };
  const saved = event => {
    for (const { node, value } of event.detail || []) {
      if (node?.isConnected && valueOf(node) === value) {
        initial.set(node, value);
        dirty.delete(node);
      }
    }
    activity();
  };
  document.addEventListener(SAVED_EVENT, saved);
  document.addEventListener('focusin', remember, true);
  document.addEventListener('pointerdown', remember, true);
  document.addEventListener('input', changed, true);
  document.addEventListener('change', changed, true);
  document.addEventListener('reset', reset, true);
  document.addEventListener('pointerdown', activity, true);
  document.addEventListener('keydown', activity, true);
  document.addEventListener('visibilitychange', visible);
  window.addEventListener('focus', visible);
  const interval = window.setInterval(check, checkInterval);
  const safetyInterval = window.setInterval(tryReload, 1000);
  void check();
  return () => {
    stopped = true;
    window.clearInterval(interval); window.clearInterval(safetyInterval);
    document.removeEventListener('focusin', remember, true);
    document.removeEventListener('pointerdown', remember, true);
    document.removeEventListener('input', changed, true);
    document.removeEventListener('change', changed, true);
    document.removeEventListener('reset', reset, true);
    document.removeEventListener(SAVED_EVENT, saved);
    document.removeEventListener('pointerdown', activity, true);
    document.removeEventListener('keydown', activity, true);
    document.removeEventListener('visibilitychange', visible);
    window.removeEventListener('focus', visible);
    if (window.fetch === trackedFetch) window.fetch = originalFetch;
    if (XMLHttpRequest.prototype.open === trackedOpen) XMLHttpRequest.prototype.open = originalOpen;
    if (XMLHttpRequest.prototype.send === trackedSend) XMLHttpRequest.prototype.send = originalSend;
  };
}
